// src/utils/preisvergabe.js
//
// Welches Team bekommt bei der Siegerehrung welchen Preis?
//
// BEHOBENER FEHLER: Die Preisverleihung nahm je Team den ERSTEN passenden
// Preis (`prices.find`). Ein "Doppeltreffer" – z. B. 1. Platz Mixed und
// zugleich 3. Platz gesamt – bekam still den Preis, der zufaellig weiter oben
// in der Preisliste stand, und der andere Preis wurde an niemanden vergeben.
//
// REGEL
//   - Jedes Team bekommt hoechstens einen Preis.
//   - Trifft ein Team mehrere Preise (in verschiedenen Wertungen), entscheidet
//     das Orga-Team vor dem Start, welchen es bekommt.
//   - In den Wertungen, auf deren Preis das Team verzichtet, wird es
//     uebersprungen: Alle dahinter ruecken einen Platz auf. Dadurch kann das
//     nachrueckende Team selbst zum Doppeltreffer werden – das wird dann als
//     naechstes gefragt.
//   - Doppeltreffer werden nach Gesamtplatz abgearbeitet (bester zuerst). Die
//     Entscheidung eines Teams verschiebt nur Teams, die in derselben Wertung
//     hinter ihm liegen – und damit auch im Gesamtplatz. Bereits getroffene
//     Entscheidungen besserer Teams bleiben dadurch gueltig.
//   - Punktgleiche Teams mit geteiltem Platz (echter Gleichstand: auch alle
//     Stationsplatzierungen gleich) teilen sich den Platz. Liegt dort ein Preis
//     (es gibt ihn nur einmal), entscheidet das Orga-Team vor dem Start, wer
//     ihn bekommt. Das andere Team rueckt einen Platz dahinter; gibt es dafuer
//     einen Preis, geht der an dieses Team. Gleichstaende, hinter denen kein
//     Preis liegt, bleiben geteilt und brauchen keine Entscheidung.
//   - Teams ohne Punkte bekommen keinen Preis.
//   - "Beide Preise behalten" ist bewusst KEINE Option (Vorgabe Orga-Team).
//
// Rein und ohne React – direkt testbar.

import { teamKey } from "./teamKey";
import { nachRang } from "./gleichstand";

const GESAMT = "gesamt";

/**
 * Schluessel einer Gleichstands-Entscheidung in der Entscheidungs-Map (neben
 * den Doppeltreffer-Eintraegen "teamKey -> Slot-ID"). Wert = teamKey des
 * Teams, das den Platz `rang` bekommt. Mit Kategorie, weil die Map fuer alle
 * Kategorien gemeinsam gespeichert wird.
 */
export const gleichstandSchluessel = (eventType, liste, rang) =>
  `gleich|${eventType}|${liste}|${rang}`;

/** Praefix aller Gleichstands-Schluessel einer Kategorie (zum Zuruecksetzen). */
export const gleichstandPraefix = (eventType) => `gleich|${eventType}|`;

/**
 * Preisregeln in konkrete "Plaetze" (Slots) einer Wertungsliste uebersetzen.
 * Eine Regel "Platz im Teamtyp" fuer "Alle" gilt in jeder Teamtyp-Liste
 * einzeln (1. Maenner, 1. Frauen, 1. Mixed) – genau so wurde sie schon bisher
 * ausgewertet.
 */
function slotsBilden(prices, teamsImEvent, eventType) {
  const typen = [...new Set(teamsImEvent.map((t) => t.team_type).filter(Boolean))];
  const slots = [];
  (prices || []).forEach((preis, i) => {
    if (preis.eventType !== eventType) return;
    // Bezug fuer gespeicherte Entscheidungen: die feste Preis-ID. Die
    // Listenposition ist nur Rueckfall fuer Preise ohne ID (Altbestand) –
    // sie verschiebt sich beim Umsortieren, eine Entscheidung zeigte dann
    // still auf einen anderen Preis.
    const bezug = preis.id ?? i;
    const platz = parseInt(preis.rank, 10);
    if (!platz || platz <= 0) return;

    if (preis.rankType === "Teamtyp") {
      const fuer = preis.teamType === "Alle" ? typen : [preis.teamType];
      for (const typ of fuer) {
        slots.push({ id: `${bezug}:typ:${typ}`, preis, liste: `typ:${typ}`, platz, typ, nurTyp: null });
      }
    } else {
      slots.push({
        id: `${bezug}:${GESAMT}`,
        preis,
        liste: GESAMT,
        platz,
        typ: null,
        // "Gesamtrang + Teamtyp" (Preisverwaltung warnt davor): der Platz zaehlt
        // im ganzen Event, vergeben wird nur, wenn der Teamtyp passt.
        nurTyp: preis.teamType && preis.teamType !== "Alle" ? preis.teamType : null,
      });
    }
  });
  return slots;
}

/**
 * Wertungslisten des Events: gesamt und je Teamtyp, mit dem zentralen Platz.
 * Teams ohne Punkte sind nicht angetreten und bekommen keinen Preis – sie
 * stehen ohnehin hinter allen anderen, die Plaetze davor aendern sich nicht.
 */
function listenBilden(teamsImEvent) {
  const listen = new Map();
  const hinzu = (name, team, rang) => {
    if (!listen.has(name)) listen.set(name, []);
    listen.get(name).push({ key: teamKey(team), team, rang });
  };
  for (const team of teamsImEvent) {
    if ((team.total_points || 0) <= 0) continue;
    if (Number.isInteger(team.rank)) hinzu(GESAMT, team, team.rank);
    if (team.team_type && Number.isInteger(team.rank_team_type)) {
      hinzu(`typ:${team.team_type}`, team, team.rank_team_type);
    }
  }
  return listen;
}

/**
 * Echte Gleichstaende an Preisen aufloesen. Die Teams einer Gruppe mit
 * geteiltem Platz `r` und Preis auf einem der Plaetze r..r+n-1 werden nach der
 * Entscheidung des Orga-Teams hintereinander gereiht (r, r+1, ...). Der Rest
 * bleibt geteilt, solange kein Preis mehr in seiner Spanne liegt.
 *
 * Aendert `rang` der Eintraege in `listen`. Gibt den ersten noch offenen
 * Gleichstand zurueck (oder null) plus die bereits entschiedenen.
 */
function gleichstaendeAufloesen(listen, slots, eventType, entscheidungen) {
  let offen = null;
  const entschieden = [];
  for (const [liste, eintraege] of listen) {
    const preisPlaetze = slots.filter((s) => s.liste === liste);
    const gruppen = new Map();
    for (const e of eintraege) {
      if (!gruppen.has(e.rang)) gruppen.set(e.rang, []);
      gruppen.get(e.rang).push(e);
    }
    for (const r of [...gruppen.keys()].sort((a, b) => a - b)) {
      let rest = gruppen.get(r).sort((a, b) => nachRang(a.team, b.team));
      let rang = r;
      while (rest.length > 1) {
        const bis = rang + rest.length - 1;
        const von = rang;
        const betroffen = preisPlaetze.filter((s) => s.platz >= von && s.platz <= bis);
        if (betroffen.length === 0) break;
        const schluessel = gleichstandSchluessel(eventType, liste, rang);
        const wahl = rest.find((e) => e.key === entscheidungen[schluessel]);
        if (!wahl) {
          if (!offen) {
            offen = {
              schluessel,
              liste,
              rang,
              kandidaten: rest.map((e) => e.team),
              keys: rest.map((e) => e.key),
              preise: betroffen,
            };
          }
          break;
        }
        wahl.rang = rang;
        entschieden.push({ liste, rang, team: wahl.team, kandidaten: rest.map((e) => e.team) });
        rest = rest.filter((e) => e !== wahl);
        rang += 1;
      }
      for (const e of rest) e.rang = rang;
    }
  }
  return { offen, entschieden };
}

/**
 * Gewinner je Slot, wenn die Teams in `aktiv` (teamKey -> gewaehlter Slot) in
 * allen anderen Wertungslisten uebersprungen werden.
 *
 * Platz nach dem Ueberspringen = 1 + Anzahl verbliebener Teams mit echt
 * besserem Platz. Geteilte Plaetze bleiben dadurch geteilt (1-2-2-4).
 */
function gewinnerBerechnen(slots, listen, aktiv) {
  const effektiv = new Map();
  for (const [name, eintraege] of listen) {
    const verbleibend = eintraege.filter((e) => {
      const wahl = aktiv.get(e.key);
      return !wahl || wahl.liste === name;
    });
    const plaetze = new Map();
    for (const e of verbleibend) {
      plaetze.set(e.key, 1 + verbleibend.filter((o) => o.rang < e.rang).length);
    }
    effektiv.set(name, plaetze);
  }

  const gewinner = new Map();
  for (const slot of slots) {
    const plaetze = effektiv.get(slot.liste) || new Map();
    gewinner.set(
      slot.id,
      (listen.get(slot.liste) || []).filter(
        (e) => plaetze.get(e.key) === slot.platz && (!slot.nurTyp || e.team.team_type === slot.nurTyp)
      )
    );
  }
  return gewinner;
}

/**
 * Preise je Team. Zwei Preise auf demselben Platz derselben Liste (doppelt
 * angelegte Regel) sind kein Doppeltreffer: wie bisher greift nur der erste.
 */
function preiseJeTeam(slots, gewinner) {
  const jeTeam = new Map();
  for (const slot of slots) {
    for (const e of gewinner.get(slot.id)) {
      const eintrag = jeTeam.get(e.key) ?? { key: e.key, team: e.team, slots: [] };
      if (!eintrag.slots.some((s) => s.liste === slot.liste)) eintrag.slots.push(slot);
      jeTeam.set(e.key, eintrag);
    }
  }
  return jeTeam;
}

/** Urspruenglicher Platz eines Teams in der Liste eines Slots. */
function urspruenglicherPlatz(team, slot) {
  return slot.liste === GESAMT ? team.rank : team.rank_team_type;
}

/** "3. Platz gesamt", "1. Platz Mixed" */
export function slotBeschreibung(slot, platz = slot.platz) {
  if (slot.liste === GESAMT) {
    return `${platz}. Platz gesamt${slot.nurTyp ? ` (nur ${slot.nurTyp})` : ""}`;
  }
  return `${platz}. Platz ${slot.typ}`;
}

/** Wie sich die Gewinner aendern, wenn `key` den Slot `wahl` nimmt. */
function folgenBerechnen(slots, listen, aktiv, gewinnerVorher, key, wahl) {
  const mitWahl = new Map(aktiv).set(key, wahl);
  const gewinnerNachher = gewinnerBerechnen(slots, listen, mitWahl);
  const jeTeamNachher = preiseJeTeam(slots, gewinnerNachher);

  const folgen = [];
  for (const slot of slots) {
    if (slot.id === wahl.id) continue;
    const vorher = gewinnerVorher.get(slot.id);
    const nachher = gewinnerNachher.get(slot.id);
    const gleich =
      vorher.length === nachher.length && vorher.every((v) => nachher.some((n) => n.key === v.key));
    if (gleich) continue;
    folgen.push({
      slot,
      verliert: vorher.some((v) => v.key === key),
      nachher: nachher.map((e) => ({
        team: e.team,
        bisher: slotBeschreibung(slot, e.rang),
        // Nachruecker trifft dadurch selbst mehrere Preise -> wird danach gefragt
        weitererDoppeltreffer: (jeTeamNachher.get(e.key)?.slots.length ?? 0) > 1,
      })),
    });
  }
  return folgen;
}

/**
 * Preise eines Events verteilen.
 *
 * `entscheidungen`: teamKey -> Slot-ID, die das Team bei einem Doppeltreffer
 * bekommt. Eine Entscheidung, deren Slot das Team (nach geaenderten
 * Ergebnissen) gar nicht mehr trifft, wird ignoriert und erneut gefragt.
 *
 * Rueckgabe:
 *   zuteilung   – Map teamKey -> { slot, nachgerueckt, bisherPlatz }
 *   entschieden – bereits entschiedene Doppeltreffer (in Abarbeitungsreihenfolge)
 *   offen       – der naechste noch zu entscheidende Doppeltreffer oder null:
 *                 { key, team, optionen: [{ slot, bisherPlatz, folgen }] }
 *                 Solange `offen` gesetzt ist, ist `zuteilung` nur vorlaeufig.
 *   gleichstandOffen / gleichstandEntschieden – echte Gleichstaende an einem
 *                 Preis: { schluessel, liste, rang, kandidaten, preise } bzw.
 *                 Liste von { liste, rang, team, kandidaten }. Wird zuerst
 *                 geklaert; solange er offen ist, ist `offen` immer null.
 */
export function preiseVerteilen(teams, prices, eventType, entscheidungen = {}) {
  const imEvent = (teams || []).filter((t) => t.event_type === eventType);
  const slots = slotsBilden(prices, imEvent, eventType);
  const listen = listenBilden(imEvent);
  const gleichstand = gleichstaendeAufloesen(listen, slots, eventType, entscheidungen);

  const aktiv = new Map();
  const entschieden = [];
  let offen = null;
  let gewinner = gewinnerBerechnen(slots, listen, aktiv);

  // Jede Runde entscheidet einen Doppeltreffer endgueltig -> hoechstens so
  // viele Runden wie Teams.
  // Ist noch ein Gleichstand offen, steht die Reihenfolge nicht fest – dann
  // waeren auch die Doppeltreffer nur geraten. Erst den Gleichstand klaeren.
  for (let runde = 0; !gleichstand.offen && runde <= imEvent.length; runde++) {
    const doppel = [...preiseJeTeam(slots, gewinner).values()]
      .filter((e) => e.slots.length > 1 && !aktiv.has(e.key))
      .sort((a, b) => nachRang(a.team, b.team));
    if (doppel.length === 0) break;

    const { key, team, slots: optionen } = doppel[0];
    const wahl = optionen.find((s) => s.id === entscheidungen[key]);
    if (!wahl) {
      const gewinnerVorher = gewinner;
      offen = {
        key,
        team,
        optionen: optionen.map((slot) => ({
          slot,
          bisherPlatz: urspruenglicherPlatz(team, slot),
          folgen: folgenBerechnen(slots, listen, aktiv, gewinnerVorher, key, slot),
        })),
      };
      break;
    }
    aktiv.set(key, wahl);
    entschieden.push({ key, team, gewaehlt: wahl, verzicht: optionen.filter((s) => s !== wahl) });
    gewinner = gewinnerBerechnen(slots, listen, aktiv);
  }

  const zuteilung = new Map();
  for (const [key, { team, slots: gewonnen }] of preiseJeTeam(slots, gewinner)) {
    const slot = aktiv.get(key) ?? gewonnen[0];
    const bisherPlatz = urspruenglicherPlatz(team, slot);
    zuteilung.set(key, { slot, nachgerueckt: bisherPlatz !== slot.platz, bisherPlatz });
  }

  return {
    zuteilung,
    entschieden,
    offen,
    gleichstandOffen: gleichstand.offen,
    gleichstandEntschieden: gleichstand.entschieden,
  };
}
